import { Button, Grid, Input, Modal, Select, Typography } from 'antd';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ZoneDropdown from '../../../components/ZonesDropdown';
import { useAuth } from '../../../hooks/useAuth';
import {
	ApplicationsFilter,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { UndoOutlined } from '@ant-design/icons';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import {
	VALID_ABHYASI_ID_REGEX,
	VALID_EMAIL_REGEX,
} from '../../../shared/Constants';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterFunctionariesProps {
	open: boolean;
	value: ApplicationsFilter;
	onClose: () => void;
	onFilterChange: (value: ApplicationsFilter) => void;
	resetFilterValues: boolean;
	setResetFilterValues: React.Dispatch<React.SetStateAction<boolean>>;
}

const { Option } = Select;

export const FilterFunctionaries = ({
	open,
	value: { searchByProposerSrcmId, zoneNames },
	onClose,
	onFilterChange,
	resetFilterValues,
	setResetFilterValues,
}: FilterFunctionariesProps) => {
	const { srcmId } = useAuth();
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = ['ta', 'ma', 'te', 'ka'].includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [proposerSrcmId, setProposerSrcmId] = useState<string | undefined>(
		searchByProposerSrcmId
	);
	const [proposerSrcmIdError, setProposerSrcmIdError] = useState('');
	const [emailIdError, setEmailIdError] = useState('');
	const [zones, setZones] = useState<Array<string> | undefined>(zoneNames);
	const [name, setName] = useState<string | undefined>('');
	const [email, setEmail] = useState<string | undefined>('');
	const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
	const { role } = useRedirectToLogin();

	const isApprover = role === UserRole.APPROVER;

	const functionaryTypeDropdown = [
		...(!isApprover
			? [
					{ label: 'Care Team', value: 'CARE_TEAM' },
					{ label: 'Approver', value: 'APPROVER' },
					{ label: 'Prework Admin', value: 'PREWORK_ADMIN' },
					{ label: 'Screening Admin', value: 'SCREENING_ADMIN' },
					{ label: 'NPS Admin', value: 'NPS_ADMIN' },
					{ label: 'ZC', value: 'ZC' },
			  ]
			: []),
		{ label: 'Proposer', value: 'FUNCTIONARY' },
	];

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setProposerSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setProposerSrcmIdError('invalidAbhyasiId');
			} else {
				setProposerSrcmIdError('');
			}
		},
		[]
	);

	const onNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setName(e.target.value);
		},
		[]
	);

	const onEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setEmail(e.target.value);
			if (value && !VALID_EMAIL_REGEX.test(value)) {
				setEmailIdError('invalidEmail');
			} else {
				setEmailIdError('');
			}
		},
		[]
	);

	const onRoleChange = useCallback((roleVal: string) => {
		setRoleFilter(roleVal);
	}, []);

	const handleZoneChange = useCallback((value: Array<{ value: string }>) => {
		setZones(value.map((z) => z.value));
	}, []);

	const fireFilterChange = useCallback(() => {
		onFilterChange({
			searchByProposerSrcmId: proposerSrcmId,
			zoneNames: zones,
			searchByFunctionaryEmail: email,
			searchByFunctionaryName: name,
			roleFilters: roleFilter && roleFilter ? [roleFilter as UserRole] : [],
		});
	}, [onFilterChange, proposerSrcmId, zones, email, name, roleFilter]);

	const handleFilterButtonClick = useCallback(() => {
		fireFilterChange();
		onClose();
	}, [fireFilterChange, onClose]);

	const handleResetFilters = useCallback(() => {
		setProposerSrcmId('');
		setZones([]);
		setName('');
		setEmail('');
		setProposerSrcmIdError('');
		setEmailIdError('');
		setRoleFilter('');
		onFilterChange({ status: ['FUNCTIONARY_ASSIGNED'] });
		onClose();
	}, [onClose, onFilterChange]);

	useEffect(() => {
		setProposerSrcmId('');
		setZones([]);
		setName('');
		setEmail('');
		setRoleFilter('');
		setResetFilterValues(false);
	}, [resetFilterValues, setResetFilterValues]);

	return (
		<div className="flex flex-col h-full min-h-0 p-[8px]">
			<Modal
				open={open}
				onCancel={onClose}
				footer={null}
				centered
				className={
					isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
				}
				closable={true}
				maskClosable={true}
			>
				<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
					{t('filter')}
				</div>
				<div className="flex flex-col md:flex-row gap-4">
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650 pb-[8px]">
							{t('name')}
						</Text>
						<Input
							className="flex min-w-[200px]"
							value={name}
							allowClear
							onChange={onNameChange}
						/>
					</div>
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650">{t('email')}</Text>
						<Input
							className="flex min-w-[200px]"
							value={email}
							allowClear
							onChange={onEmailChange}
						/>
						{emailIdError && (
							<Text className="text-xs text-red-500">{t(emailIdError)}</Text>
						)}
					</div>
				</div>
				<div className="flex flex-col md:flex-row gap-4 my-2">
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650 pb-[8px]">
							{t('SRCMID')}
						</Text>
						<Input
							className="flex min-w-[200px]"
							value={proposerSrcmId}
							allowClear
							onChange={onProposerSRCMIdChange}
						/>
						{proposerSrcmIdError && (
							<Text className="text-xs text-red-500">
								{t(proposerSrcmIdError)}
							</Text>
						)}
					</div>
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text className="block text-xs text-gray-650">
							{t('byResponsibleZone')}
						</Text>
						<ZoneDropdown
							functionarySrcmId={srcmId}
							value={zones}
							onChange={handleZoneChange}
						/>
					</div>
				</div>

				{!isApprover ? (
					<div className="flex flex-col md:flex-row gap-4">
						<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
							<Text className="block text-xs text-gray-650 pb-[8px]">
								{t('functionaryType')}
							</Text>
							<Select
								showSearch
								defaultActiveFirstOption={false}
								showArrow={false}
								filterOption={false}
								onChange={onRoleChange}
								value={roleFilter}
							>
								{functionaryTypeDropdown.map((option) => (
									<Option key={option.value} value={option.value}>
										{option.label}
									</Option>
								))}
							</Select>
						</div>
					</div>
				) : null}
				<div className="flex flex-col items-center space-y-2 gap-3 md:flex-row md:justify-center md:gap-2 mt-4 ant-modal-footer">
					<Button
						className="w-full md:flex-1 h-9 text-primary-blue font-sans font-bold"
						type="link"
						onClick={handleResetFilters}
					>
						<span>
							{t('resetFilters')}
							<UndoOutlined className="ml-2" />
						</span>
					</Button>
					<Button
						className={`w-full md:flex-1 h-9 text-primary-blue font-sans font-bold ${
							isStackVertical ? 'p-2' : ''
						}`}
						onClick={onClose}
					>
						{t('closeFilter')}
					</Button>
					<Button
						type="primary"
						onClick={handleFilterButtonClick}
						className={`w-full md:flex-1 h-9 font-sans font-bold ${
							isStackVertical ? 'p-2 mt-3' : ''
						}`}
						disabled={proposerSrcmIdError || emailIdError ? true : false}
					>
						{t('applyFilter')}
					</Button>
				</div>
			</Modal>
		</div>
	);
};
